import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { NUMBER_TYPES, DATE_TYPES, isPicklistField } from '../fieldTypeHelpers';
import { useQuery } from '@tanstack/react-query';
import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { getDBColFromDataIndex } from '../common.js';
import { queryClient } from '../constants.js';
import { commonQueryProps } from './cache.js';
import { getRelationships, useRelationships } from './relationships.js';
import { getPicklists, usePicklists } from './picklists.js';
import type { 
  TableField, 
  EnrichedTableField, 
  ProcessedFieldMetadata,
  ProcessedRelationshipMetadata,
  ProcessedPicklistMetadata
} from '../../types/metadata.js';
import type { FormField } from '../../types/ui.js';
import type { CachedQueryResult } from '../../types/supabase.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

/**
 * Builds the Supabase query for fetching field metadata
 * @returns PostgrestBuilder query for table_field with all necessary columns
 */
export const buildQueryForFields = () => {
  const fieldColumns: string[] = [
    "id",
    "name",
    "table_info",
    "table_info:table_info!inner(table_name,id)",
    "data_type",
    "is_email",
    "is_phone",
    "is_file",
    "is_required",
    "is_calculated",
    "is_system",
    "is_unique",
    "is_searchable",
    "is_indexed",
    "ui_label",
    "ui_help",
    "ui_is_currency",
    "ui_is_percentage",
    "ui_precision",
    "ui_scale",
    "ui_text_type",
    "ui_data",
    "default_value",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by"
  ];
  return supabaseCoreClient
    .from('table_field')
    .select(fieldColumns.join(','));
};

// Must not exceed PostgREST max_rows in any environment (supabase/config.toml).
export const FIELD_PAGE_SIZE = 2000;
export const FIELD_PAGE_COUNT = 4;

/**
 * Reads every metadata field in FIELD_PAGE_COUNT parallel offset pages ordered
 * by id, which is several times faster than walking pages one after another.
 *
 * Accepted edge case: fields created or deleted while the pages load can shift
 * offsets, so one load may miss or repeat a field. Metadata changes are rare and
 * every change invalidates this query, so the next load corrects it.
 *
 * Fails instead of returning a partial registry when the last page has rows
 * (more than FIELD_PAGE_SIZE * FIELD_PAGE_COUNT fields) or a short page is
 * followed by rows (the server's row cap is below FIELD_PAGE_SIZE).
 */
export const fetchAllFields = async (signal?: AbortSignal) => {
  const responses = await Promise.all(Array.from({ length: FIELD_PAGE_COUNT }, (_, page) => {
    const query = buildQueryForFields().order('id')
      .range(page * FIELD_PAGE_SIZE, (page + 1) * FIELD_PAGE_SIZE - 1);
    return signal ? query.abortSignal(signal) : query;
  }));
  const error = responses.find((response) => response.error)?.error;
  if (error) throw error;
  const pages = responses.map((response) => (response.data ?? []) as unknown as TableField[]);
  if (pages[FIELD_PAGE_COUNT - 1].length) {
    throw new Error(`More than ${FIELD_PAGE_SIZE * FIELD_PAGE_COUNT} field definitions; raise FIELD_PAGE_COUNT`);
  }
  const shortPage = pages.findIndex((page) => page.length < FIELD_PAGE_SIZE);
  if (pages.slice(shortPage + 1).some((page) => page.length)) {
    throw new Error(`Field page ${shortPage} returned ${pages[shortPage].length} rows; the API row cap is below ${FIELD_PAGE_SIZE}`);
  }
  return { ...responses[0], data: pages.flat() };
};

/**
 * Enriches a field object with database column information
 * @param field - The field object to enrich
 * @param fieldData - Field metadata from useGlobalMetadata  
 * @param tableName - The table name to look up fields for
 * @returns Enriched field object with dbCol property
 */
export const enrichFieldWithDbCol = (
  field: any, 
  fieldData: ProcessedFieldMetadata, 
  tableName: string
): any => {
  const enrichedField = { ...field };
  
  const fieldName = field.dataIndex || field.field;
  
  if (!fieldName) {
    throw new Error(`[enrichFieldWithDbCol] Field missing required 'dataIndex' or 'field' property. This indicates corrupted UI configuration data: ${JSON.stringify(field)}`);
  }
  
  if (fieldData) {
    const dbCol = getDBColFromDataIndex(
      fieldName,
      fieldData,
      tableName
    );
    enrichedField.dbCol = dbCol || undefined;
  }
  
  return enrichedField;
};

/**
 * Enriches an array of fields with database column information
 * @param fields - Array of field objects to enrich
 * @param fieldData - Field metadata from useGlobalMetadata
 * @param tableName - The table name to look up fields for
 * @returns Array of enriched field objects
 */
export const enrichFieldsWithDbCol = (
  fields: any[] | null | undefined, 
  fieldData: ProcessedFieldMetadata, 
  tableName: string
): any[] | null | undefined => {
  if (!fields || !Array.isArray(fields)) {
    return fields;
  }
  
  return fields.map(field => enrichFieldWithDbCol(field, fieldData, tableName));
};

// Type for field filter function
type FieldFilterFunction = (
  field: FormField, 
  tableName: string, 
  fieldData: ProcessedFieldMetadata
) => boolean;

// Options for UI config enrichment
interface EnrichUIConfigOptions {
  fieldFilter?: FieldFilterFunction;
}

/**
 * Extracts and parses the default value for a field based on its metadata
 * @param field - The enriched field metadata
 * @returns The parsed default value or undefined
 */
export const getFieldDefaultValue = (field: EnrichedTableField): any => {
  if (!field.default_value) return undefined;

  const defaultValueObj = field.default_value as any;
  const rawValue = defaultValueObj.value;

  if (rawValue === undefined || rawValue === null) return undefined;

  // Handle special "now" value for dates
  if (
    DATE_TYPES.includes(field.data_type as any) &&
    typeof rawValue === 'string' &&
    rawValue.toLowerCase() === 'now'
  ) {
    if (field.data_type === 'date') {
      return new Date().toISOString().split('T')[0];
    }
    return new Date().toISOString();
  }

  // Handle booleans
  if (field.data_type === 'boolean') {
    if (typeof rawValue === 'string') {
      const lower = rawValue.toLowerCase();
      return lower === 'true' || lower === 't' || lower === '1';
    }
    return !!rawValue;
  }

  // Handle numbers
  if (NUMBER_TYPES.includes(field.data_type as any)) {
    const num = Number(rawValue);
    if (isNaN(num)) {
      console.warn(`Invalid number default value "${rawValue}" for field ${field.name}`);
      return undefined;
    }
    return num;
  }

  // Handle picklist fields (both single and multi-select)
  if (isPicklistField(field)) {
    // For multi-select picklists, the value might be an array
    if (field.picklist.type === 'Multi Select') {
      // If rawValue is a string, try to parse it as JSON array
      if (typeof rawValue === 'string') {
        try {
          const parsed = JSON.parse(rawValue);
          return Array.isArray(parsed) ? parsed : [rawValue];
        } catch {
          // If not valid JSON, treat as single value
          return [rawValue];
        }
      }
      // If already an array, return as-is
      return Array.isArray(rawValue) ? rawValue : [rawValue];
    }
    // For single select, return the value as-is
    return rawValue;
  }

  // Handle UUID - ensure it's a string
  if (field.data_type === 'uuid') {
    return String(rawValue);
  }

  // Handle JSON/JSONB - parse if it's a string
  if (field.data_type === 'jsonb' || field.data_type === 'json') {
    if (typeof rawValue === 'string') {
      try {
        return JSON.parse(rawValue);
      } catch {
        console.warn(`Invalid JSON default value "${rawValue}" for field ${field.name}`);
        return undefined;
      }
    }
    return rawValue;
  }

  // For text and other types, return as-is
  return rawValue;
};

/**
 * Applies default values to a record object based on table fields
 * @param tableName - The name of the table
 * @param fieldData - Processed field metadata
 * @returns An object containing default values for the table
 */
export const applyDefaultValues = (
  tableName: string,
  fieldData: ProcessedFieldMetadata
): Record<string, any> => {
  const defaults: Record<string, any> = {};
  if (!fieldData || !tableName) {
    return defaults;
  }
  
  const tableData = (fieldData as any)[tableName];
  if (!tableData || !tableData.asList) {
    return defaults;
  }
  
  const tableFields = tableData.asList as EnrichedTableField[];

  tableFields.forEach((field: EnrichedTableField) => {
    const defaultValue = getFieldDefaultValue(field);
    if (defaultValue !== undefined && field.name) {
      defaults[field.name] = defaultValue;
    }
  });

  return defaults;
};

/**
 * Recursively traverses and enriches any UI config structure containing field arrays
 * @param config - The UI configuration object or array to process
 * @param fieldData - Field metadata from useGlobalMetadata
 * @param defaultTableName - Default table name to use for field lookups
 * @param options - Additional processing options
 * @returns Enriched configuration with all field arrays processed
 */
export const enrichUIConfigWithFields = <T>(
  config: T, 
  fieldData: ProcessedFieldMetadata, 
  defaultTableName: string, 
  options: EnrichUIConfigOptions = {}
): T => {
  if (!config) {
    return config;
  }

  const { fieldFilter } = options;

  // Handle arrays
  if (Array.isArray(config)) {
    return config.map(item => enrichUIConfigWithFields(item, fieldData, defaultTableName, options)) as T;
  }

  // Handle primitive values
  if (typeof config !== 'object') {
    return config;
  }

  // Clone the object to avoid mutations
  const enrichedConfig = { ...config } as any;

  // Determine the table name for this context
  const tableName = (enrichedConfig.table_name as string) || defaultTableName;

  // If this object has a 'fields' array, process it
  if (enrichedConfig.fields && Array.isArray(enrichedConfig.fields)) {
    let fields = enrichedConfig.fields as FormField[];
    
    // Apply filtering if provided
    if (fieldFilter) {
      fields = fields.filter(field => fieldFilter(field, tableName, fieldData));
    }
    
    // Enrich the fields
    enrichedConfig.fields = enrichFieldsWithDbCol(fields, fieldData, tableName);
  }

  // Recursively process all other properties
  Object.keys(enrichedConfig).forEach(key => {
    if (key !== 'fields') { // Skip fields since we already processed it
      enrichedConfig[key] = enrichUIConfigWithFields(enrichedConfig[key], fieldData, tableName, options);
    }
  });

  return enrichedConfig as T;
};

/**
 * Post-processes field data by enriching with relationships and picklists
 * @param fieldData - Raw field data from database
 * @param relData - Processed relationship metadata
 * @param picklistData - Processed picklist metadata
 * @returns Processed field metadata grouped by table
 */
export const postProcessFields = (
  fieldData: TableField[], 
  relData: ProcessedRelationshipMetadata, 
  picklistData: ProcessedPicklistMetadata
): ProcessedFieldMetadata => {
  const groupedByTable: ProcessedFieldMetadata = {
    flattened: {},
    byId: {},
    asList: []
  };

  fieldData.forEach(column => {
    // Cast to any to handle the joined table_info object
    const columnEnriched: EnrichedTableField = { ...column } as any;
    columnEnriched.table_name = (column as any).table_info?.table_name || '';
    
    if (columnEnriched.table_name && columnEnriched.name) {
      const tableName = columnEnriched.table_name;
      const fieldName = columnEnriched.name;

      if (
        relData[tableName] &&
        relData[tableName].byFieldName[fieldName]
      ) {
        columnEnriched.relationship =
          relData[tableName].byFieldName[fieldName];
      }
      
      if (
        picklistData[tableName] &&
        picklistData[tableName].byFieldName[fieldName]
      ) {
        columnEnriched.picklist =
          picklistData[tableName].byFieldName[fieldName];
      }
      
      if (!groupedByTable[tableName]) {
        groupedByTable[tableName] = { asList: [], byFieldName: {} };
      }
      
      groupedByTable[tableName].asList.push(columnEnriched);
      groupedByTable[tableName].byFieldName[fieldName] = columnEnriched;
      groupedByTable.flattened[`${tableName}_${columnEnriched.id}`] = columnEnriched;
    }
    groupedByTable.byId[columnEnriched.id] = columnEnriched;
    groupedByTable.asList.push(columnEnriched);
  });

  return groupedByTable;
};

// Result type for field operations
interface FieldResult {
  data: ProcessedFieldMetadata | null;
  error: any;
}

/**
 * Fetches fields data outside of React context
 * @returns Promise with processed field metadata or error
 */
// table_field's fingerprint includes table_info, which its select embeds.
const FIELD_QUERY_META = persistedRelations('core_crm.table_field');

export const getFields = async (): Promise<FieldResult> => {
  const fieldQuery = buildQueryForFields();
  // get the data from the cache if it exists.
  let fieldData: TableField[] | null = null;
  let fieldError: any = null;
  
  try{
    // TypeScript cannot infer the Supabase cache structure - see SupabaseCacheData type definition
    const cachedResult = queryClient.getQueryData(encode(fieldQuery, false)) as CachedQueryResult<TableField[]>;
    fieldData = cachedResult?.data ?? null;
  } catch {
    // Cache miss or error, will fetch from API
  }
  
  if (!fieldData) {
    try {
      const response = await queryClient.fetchQuery({
        queryKey: encode(fieldQuery, false), queryFn: ({ signal }) => fetchAllFields(signal), ...commonQueryProps,
        meta: FIELD_QUERY_META,
      });
      fieldData = response.data;
    } catch (error) {
      fieldError = error;
    }
  }

  if (fieldError || !fieldData) {
    return { data: null, error: fieldError || new Error('No field data found') };
  }

  // Fetch relationships and picklists
  const { data: relData, error: relError } = await getRelationships();
  if (relError || !relData) {
    return { data: null, error: relError || new Error('No relationship data found') };
  }

  const { data: picklistData, error: picklistError } = await getPicklists();
  if (picklistError || !picklistData) {
    return { data: null, error: picklistError || new Error('No picklist data found') };
  }

  const processedData = postProcessFields(fieldData as TableField[], relData, picklistData);
  return { data: processedData, error: null };
};

// Hook options interface
interface UseFieldsOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseFieldsResult {
  data: ProcessedFieldMetadata | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch fields data using React Query
 * @param options - Options including enabled flag
 * @returns Field data with loading and error states
 */
export const useFields = ({ enabled }: UseFieldsOptions): UseFieldsResult => {
  const bootReady = useMetadataBootReady();
  const fieldQuery = buildQueryForFields();
  const {
    data: fieldResponse,
    error: fieldError,
    isPending: fieldIsPending
  } = useQuery({
      queryKey: encode(fieldQuery, false),
      queryFn: ({ signal }) => fetchAllFields(signal),
      ...commonQueryProps,
      enabled: enabled && bootReady,
      meta: FIELD_QUERY_META,
  });
  const fieldData = fieldResponse?.data;

  const {
    data: relData,
    error: relError,
    isLoading: relIsLoading
  } = useRelationships({ enabled });

  const {
    data: picklistData,
    error: picklistError,
    isLoading: picklistIsLoading
  } = usePicklists({ enabled });

  // Memoized on the source query results so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(
    () => (fieldData && relData && picklistData
      ? postProcessFields(fieldData as unknown as TableField[], relData, picklistData)
      : null),
    [fieldData, relData, picklistData]
  );

  if (fieldIsPending || relIsLoading || picklistIsLoading) {
    return {
      data: null,
      error: fieldError || relError || picklistError,
      isLoading: true
    };
  }

  if (fieldError || relError || picklistError || !fieldData || !relData || !picklistData) {
    return {
      data: null,
      error: fieldError || relError || picklistError,
      isLoading: false
    };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false
  };
};
